import React, { useState, useEffect } from 'react';
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  Dialog,
} from '@shadcn/components/ui/dialog';
import { Button } from '@shadcn/components/ui/button';
import { Label } from '@shadcn/components/ui/label';
import { Badge } from '@shadcn/components/ui/badge';
import DraggableLocationList from './DraggableLocationList';
import superagent from 'superagent';
import { backendUrl } from '@helpers/consts';

const EditConfigModal = ({ config, locationsByRegion, onSuccess, isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    locationOrder: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableLocations, setAvailableLocations] = useState([]);

  useEffect(() => {
    if (config && locationsByRegion) {
      if (config.region && config.region !== 'ALL') {
       
        const regionLocations = locationsByRegion[config.region] || [];
        setAvailableLocations(regionLocations.map(loc => loc.name));
      } else {
       
        const allLocations = Object.values(locationsByRegion)
          .flat()
          .map(loc => loc.name);
        const uniqueLocations = [...new Set(allLocations)];
        setAvailableLocations(uniqueLocations);
      }
      setFormData({
        locationOrder: config.locationOrder || [],
      });
    }
  }, [config, locationsByRegion]);

  const handleLocationOrderChange = (newOrder) => {
    setFormData(prev => ({ ...prev, locationOrder: newOrder }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.locationOrder.length === 0) {
      alert('Please arrange at least one location');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await superagent
        .put(`${backendUrl}/locationOrderConfig/${config._id}`)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .send({
          locationOrder: formData.locationOrder,
        });
      
      onSuccess();
    } catch (error) {
      console.error('Error updating config:', error);
      alert('An error occurred while updating the configuration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRegionColor = (region) => {
    switch (region) {
      case 'AU': return 'bg-blue-100 text-blue-800';
      case 'US': return 'bg-green-100 text-green-800';
      case 'UK': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!config) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Edit Location Order Configuration</DialogTitle>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-6 py-4">
        <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-600">Region</Label>
            {config.region && config.region !== 'ALL' ? (
              <Badge className={getRegionColor(config.region)}>
                {config.region}
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-gray-100 text-gray-800">
                All Regions
              </Badge>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-600">Type</Label>
            <Badge variant="outline">
              {config.type}
            </Badge>
          </div>
        </div>

        {availableLocations.length > 0 && (
          <div className="space-y-3">
            <Label>Arrange Location Order</Label>
            <div className="text-sm text-gray-600 mb-2">
              {config.region && config.region !== 'ALL'
                ? `Drag and drop to rearrange the order of locations for region ${config.region}`
                : 'Drag and drop to rearrange the order of locations for all regions'
              }
            </div>
            <DraggableLocationList
              availableLocations={availableLocations}
              selectedLocations={formData.locationOrder}
              onLocationOrderChange={handleLocationOrderChange}
            />
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting || formData.locationOrder.length === 0}
          >
            {isSubmitting ? 'Updating...' : 'Update Configuration'}
          </Button>
        </div>
      </form>
    </DialogContent>
    </Dialog>
  );
};

export default EditConfigModal; 
